import { FC, useEffect } from 'react';
import { css } from '@emotion/react';
import { TaskType } from '@weave/schema-gen-ts/dist/schemas/task-center/shared/v1/enums.pb';
import { CallIntelligenceV2 } from '@frontend/api-call-intel';
import { useCallIntelDefaultsShallowStore } from '@frontend/call-intel-core';
import { useTranslation } from '@frontend/i18n';
import { ContentLoader, ListRow, NakedUl, SwitchField, useForm } from '@frontend/design-system';
import { TASK_TYPE_LIST } from '../../constants';
import { UnauthorizedAccess } from '../unauthorized-access';

interface CreateTaskSettingsFormProps {
  values?: Pick<CallIntelligenceV2.Types.AITaskSetting, 'aiTaskCategories' | 'aiTaskCreationEnabled'>;
  mixedValues?: Pick<CallIntelligenceV2.Types.AITaskSetting, 'aiTaskCategories' | 'aiTaskCreationEnabled'>;
  isAuthorized?: boolean;
  isLoading?: boolean;
  onToggle?: (
    values?: Pick<CallIntelligenceV2.Types.AITaskSetting, 'aiTaskCategories' | 'aiTaskCreationEnabled'>
  ) => void;
}

export const CreateTaskSettingsForm: FC<CreateTaskSettingsFormProps> = ({
  values,
  mixedValues,
  isAuthorized,
  isLoading,
  onToggle,
}) => {
  const { descriptionLabelsMap, dataLabels } = useCallIntelDefaultsShallowStore('descriptionLabelsMap', 'dataLabels');
  const { t } = useTranslation('settings', { keyPrefix: 'call-intel-settings' });

  const taskTypeDescriptionMap = descriptionLabelsMap?.tasks || {};
  const taskTypeLabelMap = dataLabels?.taskTypes || {};

  const {
    formProps,
    values: formValues,
    getFieldProps,
    seedValues,
  } = useForm({
    fields: {
      aiTaskCreationEnabled: {
        type: 'switch',
        value: values?.aiTaskCreationEnabled || false,
      },
      [TaskType.TYPE_SCHEDULING]: {
        type: 'switch',
        value: values?.aiTaskCategories?.[TaskType.TYPE_SCHEDULING] || false,
      },
      [TaskType.TYPE_INSURANCE]: {
        type: 'switch',
        value: values?.aiTaskCategories?.[TaskType.TYPE_INSURANCE] || false,
      },
      [TaskType.TYPE_WAITLIST]: {
        type: 'switch',
        value: values?.aiTaskCategories?.[TaskType.TYPE_WAITLIST] || false,
      },
      [TaskType.TYPE_PATIENT_CARE]: {
        type: 'switch',
        value: values?.aiTaskCategories?.[TaskType.TYPE_PATIENT_CARE] || false,
      },
      [TaskType.TYPE_BILLING]: {
        type: 'switch',
        value: values?.aiTaskCategories?.[TaskType.TYPE_BILLING] || false,
      },
      [TaskType.TYPE_OTHER]: {
        type: 'switch',
        value: values?.aiTaskCategories?.[TaskType.TYPE_OTHER] || false,
      },
    },
  });

  useEffect(() => {
    // Check if values have changed and update form accordingly
    if (values) {
      const hasAiTaskCreationChanged = (values.aiTaskCreationEnabled ?? false) !== formValues.aiTaskCreationEnabled;

      const hasTaskCategoriesChanged =
        values.aiTaskCategories &&
        TASK_TYPE_LIST.some((taskType) => {
          const newValue = values.aiTaskCategories?.[taskType] ?? false;
          const currentValue = formValues[taskType] ?? false;
          return newValue !== currentValue;
        });

      if (hasAiTaskCreationChanged || hasTaskCategoriesChanged) {
        const updatedValues: Record<string, boolean> = {};

        if (hasAiTaskCreationChanged) {
          updatedValues.aiTaskCreationEnabled = values.aiTaskCreationEnabled || false;
        }

        if (hasTaskCategoriesChanged && values.aiTaskCategories) {
          TASK_TYPE_LIST.forEach((taskType) => {
            const newValue = values.aiTaskCategories?.[taskType] ?? false;
            if (newValue !== formValues[taskType]) {
              updatedValues[taskType] = newValue;
            }
          });
        }

        seedValues(updatedValues);
        return;
      }
    }
  }, [values]);

  if (!isAuthorized) {
    return <UnauthorizedAccess />;
  }

  return (
    <form css={defaultStyles.formContainer} {...formProps}>
      <ContentLoader show={isLoading} />
      <NakedUl>
        <ListRow
          data-testid='create-task-setting-row'
          css={!formValues.aiTaskCreationEnabled && defaultStyles.noBorder}
        >
          <ListRow.Content>
            <ListRow.Content.Title>{t('Create Call Intelligence Tasks')}</ListRow.Content.Title>
            <ListRow.Content.Subtitle>
              {t('Enable Call Intelligence to create new tasks after the end of every call.')}
            </ListRow.Content.Subtitle>
          </ListRow.Content>
          <ListRow.Trail>
            <SwitchField
              label=''
              {...getFieldProps('aiTaskCreationEnabled')}
              indeterminate={mixedValues?.aiTaskCreationEnabled}
              onChange={(e) => {
                getFieldProps('aiTaskCreationEnabled').onChange(e);
                onToggle?.({
                  aiTaskCreationEnabled: (e as { value: boolean }).value,
                });
              }}
            />
          </ListRow.Trail>
        </ListRow>
      </NakedUl>

      {formValues.aiTaskCreationEnabled && (
        <NakedUl css={defaultStyles.taskCategories}>
          {TASK_TYPE_LIST.map((taskType) => (
            <ListRow key={taskType} css={defaultStyles.taskCategoryRow} data-testid={`task-category-row-${taskType}`}>
              <ListRow.Content>
                <ListRow.Content.Title>
                  {t('{{taskType}} Tasks', { taskType: taskTypeLabelMap?.[taskType] || taskType })}
                </ListRow.Content.Title>
                <ListRow.Content.Subtitle>{taskTypeDescriptionMap?.[taskType] ?? ''}</ListRow.Content.Subtitle>
              </ListRow.Content>
              <ListRow.Trail>
                <SwitchField
                  label=''
                  {...getFieldProps(taskType)}
                  indeterminate={mixedValues?.aiTaskCategories?.[taskType]}
                  onChange={(e) => {
                    getFieldProps(taskType).onChange(e);
                    onToggle?.({
                      aiTaskCategories: {
                        [taskType]: (e as { value: boolean }).value,
                      },
                    });
                  }}
                />
              </ListRow.Trail>
            </ListRow>
          ))}
        </NakedUl>
      )}
    </form>
  );
};

const defaultStyles = {
  formContainer: css`
    display: flex;
    flex-direction: column;
    position: relative;
  `,
  noBorder: css`
    border-bottom: none;
  `,
  taskCategories: css`
    padding: 8px 16px 0px 32px;
  `,
  taskCategoryRow: css`
    padding: 8px 0px;
    border-bottom: none;
  `,
};
